/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { Col, Row } from 'antd';
import { useParams } from 'react-router-dom';
import uniqBy from 'lodash/uniqBy';

import { Block, ErrorMessage } from '@atoms';
import {
  SlugField,
  TitleField,
  TextField,
  EventField,
  CompetitionField,
  PageField,
  CheckboxField,
  MinisiteField,
  LanguageSelector,
  PageLanguageSelect,
  CampaignField,
  AvailableLanguages,
} from '@molecules';
import { NO_MARGIN_BOTTOM, NO_MARGIN_TOP } from '@constants';
import ROUTES from '@constants/routes';
import { PAGE_TYPE } from '@api/pages/constants';
import { COMPETITION_CATEGORY } from '@api/competitions/constants';
import { useWatchFormValues, useIsLOCadmin, useLOCevent, useEventColorsAtom } from '@hooks';
import { showConfirm } from '@utils/modal';
import { LIVE_BLOG } from '@constants/contentModules';

import Form from '../Form';
import ContentModulesField from '../ContentModulesField';

import { FORM_FIELD, FORM_FIELDS, FORM_VALUES_TO_WATCH, DEFAULT_VALUES, KIDS_ATHLETICS } from './constants';
import PageTypeField from './PageTypeField';

const MainBlock = ({
  form,
  formValues,
  initialValues,
  pageId,
  eventId,
  locale,
  showLanguageSelectKA,
  showLanguageSelectDefault,
  isPageCreate,
  availableLanguages,
  selectLanguageReloadContent,
}) => {
  const isLOCAdmin = useIsLOCadmin();
  const locEvent = useLOCevent();

  const initialParentPage = initialValues[FORM_FIELD.PARENT_PAGE.name];
  const initialCompetition = initialValues[FORM_FIELD.COMPETITION.name];
  const initialEvent = initialValues[FORM_FIELD.EVENT.name];
  const initialMinisite = initialValues[FORM_FIELD.MINISITE.name];

  const parentPage = formValues[FORM_FIELD.PARENT_PAGE.name];
  const pageType = formValues[FORM_FIELD.PAGE_TYPE.name];
  const competition = formValues[FORM_FIELD.COMPETITION.name];

  const isCompetitionsLandingPage = pageType?.id === PAGE_TYPE.COMPETITION_LANDING.id;
  const isMinisiteLandingPage = pageType?.id === PAGE_TYPE.MINISITE_LANDING.id;
  const isMinisitePage = pageType?.id === PAGE_TYPE.MINISITE.id;
  const showEventsField = isCompetitionsLandingPage || isLOCAdmin;
  const firstLoadEnglishOnly = !pageId;

  const KALanguages = ['pt', 'ar', 'tr', 'ru'];

  if (availableLanguages) KALanguages.push(...availableLanguages);

  return (
    <Block spaced>
      <Row gutter={16}>
        <Col span={17}>
          <PageField
            {...FORM_FIELD.PARENT_PAGE}
            rules={[{ required: isLOCAdmin && !isCompetitionsLandingPage, message: 'Parent page is required' }]}
            initialParentPage={initialParentPage}
            queryVariables={{ eventId: locEvent?.id }}
            wrapperCol={{ span: 22 }}
            filterList={(parentPages) => {
              const filteredByPageId = parentPages.filter((page) => page.contentId !== pageId);
              const filteredByUniq = uniqBy(filteredByPageId, 'id');
              return filteredByUniq;
            }}
            locale={locale || 'en'}
          />
        </Col>
        <Col span={7}>
          {/* 
          This will show either the default LANGUAGE picker for 'en' or event based picker or KA picker  */}

          {showLanguageSelectDefault && (
            <>
              <PageLanguageSelect
                eventId={eventId}
                locale={locale}
                form={form}
                field={FORM_FIELD.PAGE_LANGUAGE}
                disabled={isPageCreate}
                style={(NO_MARGIN_BOTTOM, NO_MARGIN_TOP)}
                hideTitle
                withInline
                selectLanguageReloadContent={selectLanguageReloadContent}
              />
              <AvailableLanguages entity={{ availableLanguages }} />
            </>
          )}

          {showLanguageSelectKA && !showLanguageSelectDefault ? (
            <>
              <LanguageSelector
                {...FORM_FIELD.PAGE_LANGUAGE}
                style={(NO_MARGIN_BOTTOM, { minWidth: '158px', float: 'right' })}
                defaultLanguage={firstLoadEnglishOnly}
              />
              {availableLanguages && (
                <div style={{ clear: 'both' }}>
                  <AvailableLanguages entity={{ availableLanguages: KALanguages }} />
                </div>
              )}
            </>
          ) : (
            <>
              <Form.Item name={FORM_FIELD.PAGE_LANGUAGE.name} style={{ display: 'none' }} />
            </>
          )}
        </Col>
      </Row>

      <PageTypeField
        style={!isCompetitionsLandingPage && !isMinisiteLandingPage && NO_MARGIN_BOTTOM}
        queryVariables={{ allowedForTypeId: parentPage?.typeId }}
        // Don't remove this. It's forbidden to change page type when page is saved
        disabled={Boolean(pageId) && !(initialParentPage !== parentPage)}
        parentPageFieldName={FORM_FIELD.PARENT_PAGE.name}
        initialParentPage={initialParentPage}
        form={form}
      />
      {isCompetitionsLandingPage && (
        <CompetitionField
          {...FORM_FIELD.COMPETITION}
          disabled={isLOCAdmin}
          initialCompetition={initialCompetition}
          queryVariables={{ category: COMPETITION_CATEGORY.WORLD_ATHLETICS_SERIES }}
        />
      )}
      {/**
       * We show this field if user is LOC admin or the page is competitions landing page.
       * But for LOC user it's hidden when the page type is not competitions landing page.
       * By doing this we pass always eventId and do not need to hassle with useVersionedEntity handleSaveDraft
       * and handlePublish with conditionally passing eventId if user is LOC both on detailed and create pages
       */}
      {showEventsField && (
        <EventField
          {...FORM_FIELD.EVENT}
          rules={isCompetitionsLandingPage ? FORM_FIELD.EVENT.rules : null}
          hidden={!isCompetitionsLandingPage}
          disabled={isLOCAdmin}
          initialEvent={initialEvent}
          queryVariables={{ subCategoryNameUrlSlug: competition?.urlSlug }}
          style={NO_MARGIN_BOTTOM}
        />
      )}
      {(isMinisiteLandingPage || isMinisitePage) && (
        <MinisiteField
          {...FORM_FIELD.MINISITE}
          hidden={isMinisitePage}
          initialEvent={initialMinisite}
          style={NO_MARGIN_BOTTOM}
        />
      )}
    </Block>
  );
};

const SeoBlock = ({ initialValues, isGated }) => {
  const isLOCadmin = useIsLOCadmin();
  return (
    <Block spaced>
      <TitleField {...FORM_FIELD.TITLE} />
      <SlugField
        {...FORM_FIELD.SLUG}
        generateSlugFrom={!initialValues[FORM_FIELD.SLUG.name] && FORM_FIELD.TITLE.name}
      />
      <TextField multiline {...FORM_FIELD.SEO_DESCRIPTION} style={isLOCadmin && NO_MARGIN_BOTTOM} />
      {!isLOCadmin && (
        <>
          <CheckboxField {...FORM_FIELD.GATED_CONTENT} style={!isGated && NO_MARGIN_BOTTOM} />

          {isGated && <CampaignField {...FORM_FIELD.CAMPAIGN} style={NO_MARGIN_BOTTOM} />}
        </>
      )}


    </Block>
  );
};

const ContentModuleBlock = ({ form, pageId, saveDraft, eventBlogsList }) => {
  const navigate = useNavigate();

  const handleAllowAdd = React.useCallback(
    ({ type }) => {
      if (pageId || type !== LIVE_BLOG) return true;

      return new Promise((resolve) => {
        showConfirm({
          title: 'Before adding live blog please save the page',
          okText: 'SAVE DRAFT',
          onOk: () =>
            saveDraft(null, {
              onSuccess: (page) => {
                resolve(false);
                navigate(`${ROUTES.PAGES}/${page.id}?contentModule=${LIVE_BLOG}`);
              },
            }),
          onCancel: () => resolve(false),
        });
      });
    },
    [navigate, pageId, saveDraft],
  );

  return (
    <ContentModulesField
      {...FORM_FIELD.CONTENT_MODULES}
      eventBlogsList={eventBlogsList}
      form={form}
      parentId={pageId}
      parentType="page"
      allowAdd={handleAllowAdd}
    />
  );
};

const PageForm = ({
  initialValues = {},
  error,
  form,
  pageId,
  saveDraft,
  isPageCreate,
  availableLanguages,
  eventBlogsList,
}) => {
  const [formValues, watchFormValues] = useWatchFormValues(FORM_VALUES_TO_WATCH, initialValues);
  const [, setEventColorId] = useEventColorsAtom();
  const LOCevent = useLOCevent();
  const { locale } = useParams();
  const [eventId, setEventId] = React.useState(LOCevent?.id);

  const [showLanguageSelectKA, setShowLanguageSelectKA] = React.useState(false);
  const [showLanguageSelectDefault, setShowLanguageSelectDefault] = React.useState(false);

  const [isGated, setIsGated] = useState(initialValues.gatedContent || false);

  const navigateToLanguage = useNavigate();
  const selectLanguageReloadContent = React.useCallback(
    (language) => {
      const location = window.location.pathname;
      const path = `/${location.split('/')[1]}/${location.split('/')[2]}/${language}`;
      navigateToLanguage(path);
    },
    [navigateToLanguage],
  );

  React.useEffect(() => {
    form.resetFields();
  }, [initialValues.language, form]);

  React.useEffect(() => {
    setEventId(formValues.event?.id);
    if (initialValues[FORM_FIELD.MINISITE.name]?.name === KIDS_ATHLETICS.name) {
      setShowLanguageSelectKA(true);
    }
  }, [formValues.competition, formValues.event, formValues.type, initialValues, setEventId, setShowLanguageSelectKA]);

  React.useEffect(() => {
    const competitionLanding = formValues.type?.name === PAGE_TYPE.COMPETITION_LANDING.name;
    const _showLanguageSelect =
      !!(formValues.type && formValues.competition && formValues.event && competitionLanding) || formValues.event?.id;
    setShowLanguageSelectDefault(_showLanguageSelect);
  }, [setShowLanguageSelectDefault, formValues.competition, formValues.event, formValues.type]);

  const handleValuesChange = React.useCallback(
    (change, allChanges) => {
      if (FORM_FIELD.MINISITE.name in change) {
        if (change[FORM_FIELD.MINISITE.name].name === KIDS_ATHLETICS.name) {
          setShowLanguageSelectKA(true);
        } else {
          setShowLanguageSelectKA(false);
        }
      }

      if (FORM_FIELD.PAGE_TYPE.name in change) {
        if (change[FORM_FIELD.PAGE_TYPE.name]?.name === 'Minisite Page') {
          setShowLanguageSelectKA(true);
        } else {
          setShowLanguageSelectKA(false);
        }
      }

      if (change.language && initialValues.language) {
        // reload page based on language being changed in the dropdown
        selectLanguageReloadContent(change.language);
      }

      if (FORM_FIELD.PAGE_TYPE.name in change) {
        const pageTypeId = change[FORM_FIELD.PAGE_TYPE.name].id;
        const isContentPage = PAGE_TYPE.CONTENT.id === pageTypeId;
        const parentPage = allChanges[FORM_FIELD.PARENT_PAGE.name];

        // clear minisite field if not set to Minisite page
        if (change[FORM_FIELD.PAGE_TYPE.name].name !== 'Minisite Page') {
          form.resetFields([FORM_FIELD.MINISITE.name]);
        }
        // clear all values after we change page type field
        form.resetFields([FORM_FIELD.COMPETITION.name, FORM_FIELD.EVENT.name]);

        if (isContentPage && parentPage) {
          setEventColorId(parentPage.eventId);
        }
      }

      if (FORM_FIELD.COMPETITION.name in change) {
        form.resetFields([FORM_FIELD.EVENT.name]);
      }

      if (FORM_FIELD.PARENT_PAGE.name in change) {
        const parentPage = change[FORM_FIELD.PARENT_PAGE.name];
        const event = allChanges[FORM_FIELD.EVENT.name];

        if (parentPage?.eventId && !event?.id) {
          setEventColorId(parentPage.eventId);
          setShowLanguageSelectDefault(true);
        } else {
          setShowLanguageSelectDefault(false);
        }

        if ([PAGE_TYPE.MINISITE_LANDING.id, PAGE_TYPE.MINISITE.id].includes(parentPage?.typeId)) {
          form.setFields([{ name: FORM_FIELD.MINISITE.name, value: { id: parentPage.siteId }, errors: [] }]);

          if (KIDS_ATHLETICS.id.indexOf(parentPage.siteId) !== -1) {
            setShowLanguageSelectKA(true);
          } else {
            setShowLanguageSelectKA(false);
          }
        }
      }

      if (FORM_FIELD.GATED_CONTENT.name in change) {
        setIsGated(change[FORM_FIELD.GATED_CONTENT.name]);
      }

      if (FORM_FIELD.EVENT.name in change) {
        const _eventId = change[FORM_FIELD.EVENT.name]?.id;

        setEventColorId(_eventId);
        form.resetFields([FORM_FIELD.PAGE_LANGUAGE.name]);
        setShowLanguageSelectDefault(true);
      }
      watchFormValues(change, allChanges);
    },
    [form, setEventColorId, watchFormValues, initialValues, selectLanguageReloadContent],
  );

  return (
    <Form
      form={form}
      hideRequiredMark
      onValuesChange={handleValuesChange}
      layout="vertical"
      wrapperCol={{ span: 24 }}
      initialValues={initialValues}
    >
      <ErrorMessage error={error} variant="block" spaced />
      <MainBlock
        formValues={formValues}
        initialValues={initialValues}
        pageId={pageId}
        locale={locale}
        eventId={eventId}
        showLanguageSelectKA={showLanguageSelectKA}
        form={form}
        isPageCreate={isPageCreate}
        availableLanguages={availableLanguages}
        showLanguageSelectDefault={showLanguageSelectDefault}
        selectLanguageReloadContent={selectLanguageReloadContent}
      />
      <SeoBlock initialValues={initialValues} isGated={isGated} />
      <ContentModuleBlock form={form} pageId={pageId} saveDraft={saveDraft} eventBlogsList={eventBlogsList} />
    </Form>
  );
};

PageForm.useForm = () => Form.useForm(FORM_FIELDS, DEFAULT_VALUES);
PageForm.FORM_FIELD = FORM_FIELD;

export default PageForm;
